// One account, two addresses that reach it, for as long as the rebrand needs.
//
// ── THE PROBLEM ───────────────────────────────────────────────────────────
// HQ logins are Supabase auth users, and a Supabase user has exactly ONE
// email. There is no second address to add: for the email provider,
// `auth.identities` is keyed (provider, provider_id) with provider_id = the
// user id, so a user cannot hold two email identities. Duplicating the user
// instead would split one person's password, sessions, clock-ins and
// notifications across two ids, which is worse than the problem.
//
// So the alias cannot live in the auth schema. It lives here, at the door: the
// address somebody types is resolved to the account that actually exists,
// before it is handed to Supabase.
//
// ── WHY BOTH DIRECTIONS ───────────────────────────────────────────────────
// Written while Workspace was midway through the rebrand and either address
// could be the real one. As of 2026-09-12 every staff record has moved to
// CURRENT_DOMAIN, so in practice this now catches muscle memory: somebody
// typing the address they used for a year. Both directions stay mapped
// anyway — the cost is one failed lookup against a domain with no account,
// and it means the mapping needed no edit as the last stragglers moved.
//
// Still live on the LEGACY side and deliberately not aliased: kiosk@ (a shared
// device, not a person) and the production staff who sign in with personal
// gmail/icloud addresses. DOMAIN_PAIRS only ever rewrites the two company
// domains, which is what keeps those safe.
//
// ── THREE COPIES ──────────────────────────────────────────────────────────
// This file is the original. Verbatim ports live at
// growth/src/lib/auth-email-alias.ts (separate Vite root, no shared package)
// and inline in mrp/login.html (static page, no build step). Change the domain
// pair in all three, or delete all three together.
//
// ── PHASING IT OUT ────────────────────────────────────────────────────────
// Delete `LEGACY_DOMAIN` from DOMAIN_PAIRS and this whole behaviour goes with
// it — one line, once the old addresses are genuinely dead. Until then the
// only cost is one extra failed lookup on a mistyped domain.

/** The address staff are moving to. */
export const CURRENT_DOMAIN = "mysundaystate.com";
/** The address they are moving from. Still live as a mail alias. */
export const LEGACY_DOMAIN = "scentafilter.com";

/**
 * Domains that are the same organisation. Only these are ever rewritten — a
 * personal gmail or icloud login must never be silently pointed somewhere else.
 */
const DOMAIN_PAIRS: Record<string, string> = {
  [LEGACY_DOMAIN]: CURRENT_DOMAIN,
  [CURRENT_DOMAIN]: LEGACY_DOMAIN,
};

/** Lowercased and trimmed, which is how auth.users stores it. */
export function normalizeEmail(input: string): string {
  return String(input ?? "").trim().toLowerCase();
}

/**
 * The addresses to try for what somebody typed, in order.
 *
 * As typed first, always. That keeps the common case one round trip, and it
 * means that where BOTH accounts exist the typed one wins rather than a guess.
 * Returns a single entry for anything not on a company domain.
 */
export function loginCandidates(input: string): string[] {
  const email = normalizeEmail(input);
  const at = email.indexOf("@");
  // Not an address we can reason about — hand it over unchanged and let auth
  // give its own answer rather than inventing one.
  if (at <= 0 || at !== email.lastIndexOf("@") || at === email.length - 1) return [email];

  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  const other = DOMAIN_PAIRS[domain];
  if (!other) return [email];

  const alias = `${local}@${other}`;
  return alias === email ? [email] : [email, alias];
}

/** True when `message` is auth refusing the credentials rather than failing. */
export function isCredentialRejection(message?: string | null): boolean {
  const m = (message ?? "").toLowerCase();
  // Only retry the alias on a genuine "no" — never on a rate limit, an outage
  // or a network error, where a second attempt makes things worse.
  return m.includes("invalid login credentials")
    || m.includes("invalid_credentials")
    || m.includes("email not confirmed");
}
